import { yup } from '@framework/validation/initYup.ts';
import { AttorneyApplicantDataTypes } from './AttorneyApplicantData.ts';
import { CompanyApplicantDataTypes } from './CompanyApplicantData.ts';
import { type ISubjectDataType, SubjectDataTypes } from './SubjectDataTypes.ts';

export enum SubjectDataType {
  // Titolare
  Applicant = 'applicant',
  // Subentrante
  Successor = 'successor',
  // Azienda
  Company = 'company',
  // Procuratore
  Attorney = 'attorney',
}

export interface ISubjectDataDirective {
  /**
   * Codice dell'attributo del titolare, es. "fiscalNumber"
   */
  code?: string | null;
  /**
   * Tipo di soggetto (titolare, subentrante, ...)
   */
  type?: SubjectDataType | null;
}

/**
 * Forma comune dei repository di attributi soggetto
 * (`SubjectDataTypes`, `AttorneyApplicantDataTypes`, ...).
 */
export interface ISubjectDataTypesGroup {
  List: ISubjectDataType[];
  Codes: readonly string[];
}

/**
 * Repository di attributi disponibili per un tipo di soggetto: unica fonte
 * di verità, usata sia per le opzioni della select di configurazione
 * (`.List`) sia per il controllo di coerenza tipo/attributo dello schema
 * (`.Codes`).
 */
export function getSubjectDataTypesForType(
  type: SubjectDataType,
): ISubjectDataTypesGroup | null {
  switch (type) {
    case SubjectDataType.Applicant:
    case SubjectDataType.Successor:
      return SubjectDataTypes;
    case SubjectDataType.Attorney:
      return AttorneyApplicantDataTypes;
    case SubjectDataType.Company:
      return CompanyApplicantDataTypes;
    default:
      return null;
  }
}

/**
 * Deriva il tipo di soggetto da un attributo.
 *
 * L'ordine di ricerca conta: gli attributi comuni (condivisi anche dal
 * Procuratore) derivano "Titolare"; il Procuratore viene derivato solo
 * dai suoi attributi specifici.
 */
function deriveSubjectDataTypeFromCode(code: string): SubjectDataType | null {
  const candidates = [
    SubjectDataType.Applicant,
    SubjectDataType.Company,
    SubjectDataType.Attorney,
  ];

  return (
    candidates.find(type =>
      getSubjectDataTypesForType(type)?.Codes.includes(code),
    ) ?? null
  );
}

/**
 * Mantiene l'invariante "attributo presente ⇒ tipo presente e coerente":
 * con tipo assente lo deriva dall'attributo, con tipo presente resetta
 * l'attributo non compatibile.
 *
 * Applicata ad ogni cast (quindi anche live dal form di configurazione,
 * via `syncComputedValues`): sostituisce il vecchio reset imperativo
 * nello `useEffect` del componente di configurazione.
 *
 * NOTA: `type === null` non può essere trattato come rimozione esplicita
 * del tipo: è anche lo stato con cui la select registra il campo vuoto.
 */
function resetIncoherentCode(value: unknown) {
  if (value == null || typeof value !== 'object') return value;
  const { code, type } = value as ISubjectDataDirective;
  if (!code) return value;

  // Tipo assente (mai impostato, config legacy o select svuotata):
  // lo deriviamo dall'attributo; attributo sconosciuto → reset
  if (type == null) {
    const derivedType = deriveSubjectDataTypeFromCode(code);
    return derivedType
      ? { ...value, type: derivedType }
      : { ...value, code: undefined };
  }

  const isCoherent =
    getSubjectDataTypesForType(type)?.Codes.includes(code) ?? false;

  return isCoherent ? value : { ...value, code: undefined };
}

export const SubjectDataDirectiveSchema: yup.ObjectSchema<ISubjectDataDirective> =
  yup
    .object({
      code: yup
        .string()
        .oneOf(
          [
            ...SubjectDataTypes.Codes,
            ...CompanyApplicantDataTypes.Codes,
            ...AttorneyApplicantDataTypes.SpecificCode,
          ],
          `L'attributo indicato non è valido`,
        )
        .notRequired()
        .label('Attributo del soggetto'),
      // La derivazione del tipo da un attributo senza tipo è gestita
      // dal transform `resetIncoherentCode` (vedi sopra)
      type: yup
        .string()
        .oneOfEnum(SubjectDataType)
        .notRequired()
        .label('Tipo di soggetto'),
    })
    .noUnknown()
    .transform(resetIncoherentCode);
